import React, { useMemo } from 'react';
import { Center } from '@chakra-ui/layout';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';

import { DeviceOption, SiteDeviceOptionSelect } from '../../../common/components/SiteDeviceOptionSelect';
import { useImportLimitedDevices, useUpdateImportLimitedDeviceMutation } from '../profileApi';
import { ProfilePageBase } from '../ProfilePageBase';

export function ImportLimitSettings() {
  const bgColor = useColorModeValue('white', 'dusk100.800');
  const toast = useToast();
  const { data: importLimitedDevices, isLoading, isError } = useImportLimitedDevices();
  const [updateImportLimitedDevice] = useUpdateImportLimitedDeviceMutation();
  const navigate = useNavigate();

  const deviceOptions = useMemo<DeviceOption[]>(() => {
    return importLimitedDevices.map((importLimitedDevice) => {
      // Convert the timezone agnostic string to a UTC timezone, then instantiate a `Date`
      const ignoreUntilDatetime = importLimitedDevice.ignore_until_datetime_utc
        ? importLimitedDevice.ignore_until_datetime_utc.replace(' ', 'T') + 'Z'
        : null;
      let isSelected = false;

      if (ignoreUntilDatetime) {
        // Item is selected if the ignore until datetime is in the future
        const utcDate = new Date(ignoreUntilDatetime);
        isSelected = utcDate.getTime() > Date.now();
      }

      return {
        siteDeviceId: importLimitedDevice.site_device_id,
        isSelected,
      };
    });
  }, [importLimitedDevices]);

  async function handleUpdateDevice(siteDeviceId: number) {
    const deviceOption = deviceOptions.find((d) => d.siteDeviceId == siteDeviceId);

    if (!deviceOption) {
      console.error(`No device option found for site device id ${siteDeviceId}`);
      toast({
        title: 'Unable to save import limited device configuration',
        description: 'Please contact support if this persists',
        status: 'error',
      });

      return;
    }

    const importLimitedDevice = importLimitedDevices.find((d) => d.site_device_id == siteDeviceId);

    if (!importLimitedDevice) {
      console.error(`No import limited device option found for site device id ${siteDeviceId}`);
      toast({
        title: 'Unable to save import limited device configuration',
        description: 'Please contact support if this persists',
        status: 'error',
      });

      return;
    }

    const oneDayFromNow = new Date();
    oneDayFromNow.setDate(oneDayFromNow.getDate() + 1);
    // Convert to UTC string, in structure expected by API
    const oneDayFromNowUTCString = oneDayFromNow.toISOString().split('.')[0].replace('T', ' ');
    const wasPreviouslySelected = deviceOption.isSelected;
    // If previously selected, we remove the ignore until, otherwise it's being ignored for 24 hours.
    const ignoreUntilDatetimeUTC = wasPreviouslySelected ? null : oneDayFromNowUTCString;

    try {
      await updateImportLimitedDevice({
        id: importLimitedDevice.id,
        body: { ignore_until_datetime_utc: ignoreUntilDatetimeUTC },
      }).unwrap();
    } catch (e) {
      console.error(e);
      toast({
        title: 'Unable to save import limited device configuration',
        description: 'Please contact support if this persists',
        status: 'error',
      });
    }
  }

  if (isError) {
    return (
      <ProfilePageBase title={'Import Limit Device Opt-out'}>
        <Alert data-testid="import-limit-opt-out-error-alert" status="error" variant="left-accent">
          <AlertIcon />
          <Box>
            <AlertTitle>Unable to fetch import limited device configuration</AlertTitle>
            <AlertDescription>Please contact support if this persists.</AlertDescription>
          </Box>
        </Alert>
      </ProfilePageBase>
    );
  }

  return (
    <ProfilePageBase title={'Import Limit Device Opt-out'}>
      <Box mx={[4, 4, 0]}>
        <Text mb={2}>
          During an import limit event, connected devices can be powered down or turned off completely to meet these
          limits.
        </Text>
        <Text mb={5}>
          All your connected devices are automatically participating in the event unless you add them below to be
          removed.
        </Text>
      </Box>

      <Text mx={[4, 4, 0]} mb={1} fontSize="xs">
        DEVICE(S) OPT-OUT
      </Text>
      <Box data-testid="import-limit-settings-content" p={3} bg={bgColor}>
        {isLoading ? (
          <CenteredLoader />
        ) : (
          <SiteDeviceOptionSelect deviceOptions={deviceOptions} onToggleDeviceSelection={handleUpdateDevice} />
        )}
      </Box>

      <Box mx={[4, 4, 0]}>
        <Text mt={3}>Your devices will revert back to its original settings when the import limit event is over.</Text>
        <Text mt={2}>By opting out, your selections will only last up to 24 hours.</Text>
      </Box>

      <Center mt={10}>
        <Button
          data-testid="import-limits-opt-out-done-btn"
          mb={2}
          rounded={20}
          colorScheme="dusk100"
          maxW={500}
          w="75%"
          onClick={() => navigate(-1)}
        >
          Done
        </Button>
      </Center>
    </ProfilePageBase>
  );
}
